import {useMemo, useState} from "react";
import {CustomFieldDefEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";
import {getEntityTypeDisplayValueAsString} from "../../../util/entity-ui-util";
import { getFormattedDatabaseCode } from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";

export type UseCustomFieldDefDataTableFilterProps = Omit<UseDataTableFilterProps<CustomFieldDefEntity>, 'filterConfig'>

export function useCustomFieldDefDataTableFilter(props: UseCustomFieldDefDataTableFilterProps): UseDataTableFilterResult<CustomFieldDefEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [labelQuery, setLabelQuery] = useState<string | undefined>(undefined);
  const [labelDebouncedQuery] = useDebouncedValue(labelQuery, 200);

  const [codeQuery, setCodeQuery] = useState<string | undefined>(undefined);
  const [codeDebouncedQuery] = useDebouncedValue(codeQuery, 200);

  const [companyNameQuery, setCompanyNameQuery] = useState<string | undefined>(undefined);
  const [companyNameDebouncedQuery] = useDebouncedValue(companyNameQuery, 200);

  const [forObjectTypeQuery, setForObjectTypeQuery] = useState<string | undefined>(undefined);
  const [forObjectTypeDebouncedQuery] = useDebouncedValue(forObjectTypeQuery, 200);

  const [dataTypeQuery, setDataTypeQuery] = useState<string | undefined>(undefined);
  const [dataTypeDebouncedQuery] = useDebouncedValue(dataTypeQuery, 200);

  const filterConfig: DataTableFilterConfig<CustomFieldDefEntity> = useMemo(() => {
    return {
      "label": {
        setQuery: setLabelQuery,
        query: labelQuery,
        debouncedQuery: labelDebouncedQuery,
        filter: ({label}, query) => !label || !label.toLowerCase().includes(query.toLowerCase())
      },
      "code": {
        setQuery: setCodeQuery,
        query: codeQuery,
        debouncedQuery: codeDebouncedQuery,
        filter: ({code}, query) => !code || !code.toLowerCase().includes(query.toLowerCase())
      },
      "companyName": {
        setQuery: setCompanyNameQuery,
        query: companyNameQuery,
        debouncedQuery: companyNameDebouncedQuery,
        filter: ({companyName}, query) => !companyName || !companyName.toLowerCase().includes(query.toLowerCase())
      },
      "forObjectType": {
        setQuery: setForObjectTypeQuery,
        query: forObjectTypeQuery,
        debouncedQuery: forObjectTypeDebouncedQuery,
        filter: ({forObjectType}, query) => {
          if (forObjectType) {
            const display = getEntityTypeDisplayValueAsString(forObjectType, t)
            return !forObjectType.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase()))
          } else {
            return false
          }
        }
      },
      "dataType": {
        setQuery: setDataTypeQuery,
        query: dataTypeQuery,
        debouncedQuery: dataTypeDebouncedQuery,
        filter: ({dataType}, query) => {
          if (dataType) {
            const display = getFormattedDatabaseCode(dataType, t)
            return !dataType.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase()))
          } else {
            return false
          }
        }
      },
    }
  }, [
    labelQuery,
    labelDebouncedQuery,
    codeQuery,
    codeDebouncedQuery,
    companyNameQuery,
    companyNameDebouncedQuery,
    forObjectTypeQuery,
    forObjectTypeDebouncedQuery,
    dataTypeQuery,
    dataTypeDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
